import React, { useState, useEffect } from 'react';
import { DatePicker, Typography, Tag, Avatar } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import dailyRecordService from '../../../services/dailyRecord/dailyRecord.service';
import { AttendanceDto } from '../../../types/attendanceDto.types';
import { exportToExcel, normalizeTurkishChars } from '../../../utils/exportUtils';
import AttendanceFilters from './components/AttendanceFilters';
import departmentService from '../../../services/department/department.service';
import shopService from '../../../services/shop/shop.service';
import { DepartmentDto } from '../../../types/department.types';
import { Store } from '../../../types/shop.types';
import { getFileUrl } from '../../../utils/file.utils';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../utils/errorHandler';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FooterSection from '../../shared/components/footerSection';
import Table from '../../shared/dataTable';
import i18n from '../../../i18n/config';
import { PermissionType } from '../../../types/user.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
// Day.js ayarları
dayjs.locale('tr');
dayjs.extend(localizedFormat);


const AttendanceReports: React.FC = () => {
  // State tanımlamaları
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceDto[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [filterLate, setFilterLate] = useState<boolean | null>(null);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [shops, setShops] = useState<Store[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedShop, setSelectedShop] = useState<string>('all');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<dayjs.Dayjs>(dayjs()); // Real-time güncelleme için
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  // Verileri getiren fonksiyon
  const fetchAttendanceRecords = async (startDate: string, endDate: string) => {
    setLoading(true);
    try {
      // Test için 1 saniyelik gecikme ekleyerek yükleme göstergesini görebilelim
      setTimeout(async () => {
        try {
          const data = await dailyRecordService.getAllDailyRecord(startDate, endDate);
          
          // DEBUG: İlk veriyi kontrol et
          if (data && data.length > 0) {
            console.log('🔍 Attendance Page - İlk satır verisi:', {
              isimSoyisim: `${data[0].user?.firstName} ${data[0].user?.lastName}`,
              sube: data[0].user?.branch,
              departman: data[0].user?.department,
              tumVeri: data[0]
            });
          }
          
          setAttendanceRecords(data);
          setFilteredRecords(data);
        } catch (error: any) {
          handleApiError(error, t);
        } finally {
          setLoading(false);
        }
      }, 1000);
    } catch (error: any) {
      handleApiError(error, t);
      setLoading(false);
    }
  };

  // Departman ve şube verilerini getir
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptData, shopData] = await Promise.all([
          departmentService.getDepartments(),
          shopService.getShops()
        ]);
        setDepartments(deptData);
        setShops(shopData);
      } catch (error: any) {
        handleApiError(error, t);
      }
    };
    fetchData();
  }, []);

  // Sayfa yüklendiğinde verileri getir
  useEffect(() => {
    const startDate = dayjs().startOf('day').format('YYYY-MM-DD');
    const endDate = dayjs().endOf('day').format('YYYY-MM-DD');
    setDateRange([dayjs(startDate), dayjs(endDate)]);
    fetchAttendanceRecords(startDate, endDate);
  }, []);

  // Real-time güncelleme için timer - hala içeride olan kullanıcılar için
  useEffect(() => {
    const hasActiveUsers = filteredRecords.some(record => !record.exitTime);
    
    if (hasActiveUsers) {
      const interval = setInterval(() => {
        setCurrentTime(dayjs()); // Her dakika güncelle
      }, 60000); // 60 saniye
      
      return () => clearInterval(interval);
    }
  }, [filteredRecords]);

  // Filtreleri uygula
  const applyFilters = () => {
    if (!attendanceRecords) return;

    let result = [...attendanceRecords];

    if (dateRange) {
      const [start, end] = dateRange;
      result = result.filter(record => {
        const recordDate = dayjs(record.entryTime);
        return recordDate.isSameOrAfter(start.startOf('day'), 'day') && recordDate.isSameOrBefore(end.endOf('day'), 'day');
      });
    }

    if (selectedDepartment !== 'all') {
      result = result.filter(record =>
        record.user?.department?.map(d => d.id.toString()).includes(selectedDepartment)
      );
    }
    if (selectedShop !== 'all') {
      result = result.filter(record =>
        record.user?.branch?.map(b => b.id.toString()).includes(selectedShop)
      );
    }

    if (selectedGender !== 'all') {
      result = result.filter(record =>
        record.user?.gender === selectedGender
      );
    }

    if (filterLate !== null) {
      result = result.filter(record => record.isLate === filterLate);
    }

    // Çalışan ismine göre arama
    if (searchText.trim()) {
      result = result.filter(record => {
        const fullName = `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.toLowerCase();
        return fullName.includes(searchText.toLowerCase().trim());
      });
    }

    setFilteredRecords(result);
  };

  useEffect(() => {
    applyFilters();
  }, [selectedDepartment, selectedShop, selectedGender, filterLate, dateRange, attendanceRecords, searchText]);

  // Filtreleri sıfırla
  const handleReset = () => {
    setDateRange(null);
    setSelectedDepartment('all');
    setSelectedShop('all');
    setSelectedGender('all');
    setFilterLate(null);
    setSearchText('');
    setFilteredRecords(attendanceRecords);
  };

  // Tarihi formatla
  const formatTime = (time: string) => {
    if (!time) return '-';
    return dayjs(time).format('HH:mm');
  };

  /** API'den "YYYY-MM-DD HH:mm:ss" formatında gelen originalExitTime için gün + ay + saat */
  const formatOriginalExitTime = (time: string) => {
    if (!time) return '-';
    const parsed = dayjs(time, 'YYYY-MM-DD HH:mm:ss', true);
    return parsed.isValid() ? parsed.format('D MMM HH:mm') : formatTime(time);
  };

  // const formatTime = (time: string) => {
  //   if (!time) return '-';
  //   try {
  //     // Eğer time string'i "T" karakterini içeriyorsa (ISO formatında olduğunu varsayıyoruz)
  //     // ve ardından en az HH:mm:ss formatını barındırıyorsa
  //     const timePart = time.split('T')[1];
  //     if (timePart) {
  //       // Sadece HH:mm:ss kısmını al
  //       const [hours, minutes, seconds] = timePart.split(':');
  //       if (hours && minutes && seconds) {
  //         // saniye kısmındaki ".sssZ" gibi ekleri temizle
  //         const cleanSeconds = seconds.split('.')[0];
  //         return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${cleanSeconds.padStart(2, '0')}`;
  //       }
  //     }
  //     // Eğer "T" yoksa veya format uygun değilse, orijinal string'i olduğu gibi döndürmeyi deneyebiliriz
  //     // veya daha spesifik bir kontrol ekleyebiliriz.
  //     // Bu kısım, time string'inin olası diğer formatlarına göre ayarlanmalı.
  //     // Örneğin, time zaten "HH:mm:ss" ise direkt onu döndür:
  //     if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
  //       return time;
  //     }
  //     if (/^\d{2}:\d{2}$/.test(time)) { // Eğer sadece HH:mm ise saniyeyi ekle
  //       return `${time}:00`;
  //     }
  //     // Uygun format bulunamazsa, belki de bir hata loglamak veya '-' döndürmek daha iyi olabilir.
  //     // console.warn(`formatTime: Alınan zaman "${time}" beklenilen formatta değil.`);
  //     return '-'; // Veya orijinal 'time' string'ini döndür: return time;
  //   } catch (error) {
  //     // console.error(`formatTime: Zaman formatlarken hata oluştu, zaman: "${time}"`, error);
  //     return '-'; // Hata durumunda güvenli bir değer döndür
  //   }
  // };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return dayjs(date).format('DD MMMM YYYY');
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '0 dk';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours} ${t('common.hours')} ${mins} ${t('common.minutes')}` : `${mins} ${t('common.minutes')}`;
  };

  // Frontend'de çalışma süresi hesaplama fonksiyonu
  const calculateWorkTime = (record: AttendanceDto): number => {
    if (!record.entryTime) return 0;
    
    const entryTime = dayjs(record.entryTime);
    const exitTime = record.exitTime ? dayjs(record.exitTime) : dayjs(); // Çıkış yoksa şu anki zaman
    
    // Çalışma süresini dakika cinsinden hesapla
    let workMinutes = exitTime.diff(entryTime, 'minute');
    
    // Tarih bilgilerini al
    const entryDate = entryTime.format('YYYY-MM-DD');
    const exitDate = exitTime.format('YYYY-MM-DD');
    
    // Eğer tarihler aynıysa, çıkışın ertesi güne sarkıp sarkmadığını kontrol et
    if (entryDate === exitDate) {
      const entryHourMinute = entryTime.hour() * 60 + entryTime.minute();
      const exitHourMinute = exitTime.hour() * 60 + exitTime.minute();
      
      // Çıkış saati giriş saatinden küçükse (gece vardiyası - örn: 17:26 giriş, 08:00 çıkış)
      // VEYA hesaplanan süre mantıksızsa (24 saatten fazla ama aynı gün - örn: 26 saat çalışma)
      if (exitHourMinute < entryHourMinute || workMinutes > 1440) {
        // Çıkışın ertesi gün olduğunu varsay ve yeniden hesapla
        workMinutes = exitTime.add(1, 'day').diff(entryTime, 'minute');
      }
    } else if (workMinutes < 0) {
      // Negatif fark varsa (çıkış tarihi giriş tarihinden önceyse), çıkışın ertesi gün olduğunu varsay
      workMinutes = exitTime.add(1, 'day').diff(entryTime, 'minute');
    }
    
    return Math.max(0, workMinutes);
  };

  // PDF dışa aktarma - Özel Giriş-Çıkış Raporu Template
  const handleExportPdf = async () => {
    // Landscape A4 PDF oluştur - UTF-8 desteği ile
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true
    });

    // Roboto font yükle (Türkçe karakter desteği için)
    try {
      const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
      const response = await fetch(fontUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      
      await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            const base64Font = reader.result.split(',')[1];
            doc.addFileToVFS('Roboto-Regular.ttf', base64Font);
            doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
            doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
            resolve(true);
          } else {
            reject(new Error('Font yüklenemedi'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      doc.setFont('Roboto');
    } catch (error) {
      console.warn('Roboto font yüklenemedi, varsayılan font kullanılacak:', error);
      // Hata durumunda devam et
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // İlk sayfa için header çiz
    const drawHeader = (isFirstPage: boolean) => {
      if (isFirstPage) {
        // Tam header (ilk sayfa)
        doc.setFillColor(62, 85, 105);
        doc.rect(0, 0, pageWidth, 35, 'F');

        // Logo/Şirket adı bölümü
        doc.setFillColor(45, 65, 85);
        doc.roundedRect(10, 8, 45, 18, 2, 2, 'F');
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.text('DINOX', 20, 19);

        // Başlık - Roboto font Türkçe karakterleri destekler
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(17);
        doc.setTextColor(255, 255, 255);
        const title = 'GİRİŞ-ÇIKIŞ KAYITLARI RAPORU';
        const titleWidth = doc.getTextWidth(title);
        doc.text(title, (pageWidth - titleWidth) / 2, 20);

        // Tarih aralığı
        if (dateRange) {
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(10);
          const dateText = `Tarih Aralığı: ${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`;
          const dateWidth = doc.getTextWidth(dateText);
          doc.text(dateText, (pageWidth - dateWidth) / 2, 28);
        }
      } else {
        // Küçük header (diğer sayfalar)
        doc.setFillColor(62, 85, 105);
        doc.rect(0, 0, pageWidth, 20, 'F');
        
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(255, 255, 255);
        const smallTitle = 'GİRİŞ-ÇIKIŞ RAPORU';
        const smallTitleWidth = doc.getTextWidth(smallTitle);
        doc.text(smallTitle, (pageWidth - smallTitleWidth) / 2, 13);
      }
    };

    // İlk sayfa header
    drawHeader(true);

    // Tablo başlıkları - Türkçe karakterli
    const tableHeaders = [
      ['Ad Soyad', 'Tarih', 'Giriş Saati', 'Çıkış Saati', 'Çalışma Süresi', 'Geç Kalma', 'Departman', 'Şube']
    ];

    // Tablo verileri - Türkçe karakterler olduğu gibi
    const tableData = filteredRecords.map(record => [
      `${record.user?.firstName || ''} ${record.user?.lastName || ''}`,
      formatDate(record.entryTime),
      formatTime(record.entryTime),
      formatTime(record.exitTime),
      formatDuration(calculateWorkTime(record)),
      record.isLate ? formatDuration(record.lateDuration) : '-',
      record.user?.department?.map(d => d.name).join(', ') || '-',
      record.user?.branch?.map(b => b.name).join(', ') || '-'
    ]);

    // Tablo oluştur - Roboto font ile
    autoTable(doc, {
      head: tableHeaders,
      body: tableData,
      startY: 40,
      theme: 'grid',
      styles: {
        font: 'Roboto',
        fontSize: 9,
        cellPadding: 4,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [40, 40, 40],
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: {
        fillColor: [62, 85, 105],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        font: 'Roboto',
        halign: 'center',
        valign: 'middle',
        cellPadding: 5
      },
      columnStyles: {
        0: { cellWidth: 42 }, // Ad Soyad
        1: { cellWidth: 28, halign: 'center' }, // Tarih
        2: { cellWidth: 26, halign: 'center' }, // Giriş
        3: { cellWidth: 26, halign: 'center' }, // Çıkış
        4: { cellWidth: 32, halign: 'center' }, // Çalışma
        5: { cellWidth: 26, halign: 'center' }, // Geç Kalma
        6: { cellWidth: 38 }, // Departman
        7: { cellWidth: 32 }  // Şube
      },
      alternateRowStyles: {
        fillColor: [245, 248, 250]
      },
      margin: { left: 10, right: 10, top: 45, bottom: 20 },
      didDrawPage: (data) => {
        const currentPage = data.pageNumber;
        
        // Header çiz (ikinci sayfadan itibaren küçük header)
        if (currentPage > 1) {
          drawHeader(false);
        }

        // Footer
        doc.setFillColor(240, 240, 240);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        
        // Sayfa numarası
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Sayfa ${currentPage}`,
          pageWidth - 25,
          pageHeight - 6
        );
        
        // Copyright
        doc.text(
          `© ${new Date().getFullYear()} DINOX - Tüm Hakları Saklıdır`,
          15,
          pageHeight - 6
        );
      },
      // İkinci sayfadan itibaren tablonun başlangıç noktası
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      showHead: 'everyPage'
    });

    // Dosyayı kaydet
    const fileName = `Giris-Cikis-Raporu-${dayjs().format('DD-MM-YYYY')}.pdf`;
    doc.save(fileName);
  };

  // Excel dışa aktarma
  const handleExportExcel = () => {
    type ExcelColumns = {
      header: string;
      property: 'name' | 'date' | 'entryTime' | 'exitTime' | 'workTime' | 'lateDuration' | 'isLate' | 'department' | 'branch';
      width?: number;
    };

    const columnsForExcel: ExcelColumns[] = [
      { header: 'Ad Soyad', property: 'name' },
      { header: 'Tarih', property: 'date' },
      { header: 'Giris Saati', property: 'entryTime' },
      { header: 'Cikis Saati', property: 'exitTime' },
      { header: 'Calisma Suresi', property: 'workTime' },
      { header: 'Gec Kalma', property: 'lateDuration' },
      { header: 'Geç Kaldı mı?', property: 'isLate' },
      { header: 'Departman', property: 'department' },
      { header: 'Sube', property: 'branch' }
    ];

    const data = filteredRecords.map(record => ({
      name: `${record.user?.firstName || ''} ${record.user?.lastName || ''}`,
      date: formatDate(record.entryTime),
      entryTime: formatTime(record.entryTime),
      exitTime: formatTime(record.exitTime),
      workTime: formatDuration(calculateWorkTime(record)), // Frontend hesaplama kullan
      lateDuration: record.isLate ? formatDuration(record.lateDuration) : '-',
      isLate: record.isLate ? 'Evet' : 'Hayır',
      department: record.user?.department?.map(d => normalizeTurkishChars(d.name)).join(', ') || '-',
      branch: record.user?.branch?.map(b => normalizeTurkishChars(b.name)).join(', ') || '-'
    }));

    exportToExcel(data, 'Giris-Cikis Kayitlari Raporu', columnsForExcel);
  };

  // Tablo sütunları
  const columns = [
    {
      title: t('attendanceReport.employee'),
      dataIndex: 'user',
      key: 'user',
      render: (user: any) => {
        // Department array ise name'lerini virgülle ayır
        const department = user?.department as any;
        const departmentName = Array.isArray(department)
          ? department.map((d: any) => d.name).join(', ')
          : department?.name || '-';
        
        return (
          <div className="d-flex align-items-center">
            <div className="avatar me-2">
              {user?.profilePicture ? (
                <Avatar src={getFileUrl(user.profilePicture)} size="large" />
              ) : (
                <Avatar src={user.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} size="large" />
              )}
            </div>
            <div>
              <Link to={all_routes.employeedetails.replace(':id', user?.id.toString() || '')}>
                <h6 className="mb-0">{user?.firstName} {user?.lastName}</h6>
                <small className="text-muted">{departmentName}</small>
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      title: t('attendanceReport.shop'),
      key: 'branch',
      render: (text: string, record: AttendanceDto) => {
        // Branch array ise name'lerini virgülle ayır
        const branch = record.user?.branch as any;
        const branchName = Array.isArray(branch)
          ? branch.map((b: any) => b.name).join(', ')
          : branch?.name || '-';
        return branchName;
      },
    },
    {
      title: t('attendanceReport.date'),
      dataIndex: 'entryTime',
      key: 'date',
      render: (text: string) => formatDate(text),
    },
    {
      title: t('attendanceReport.entryTime'),
      dataIndex: 'entryTime',
      key: 'entryTime',
      render: (text: string) => formatTime(text),
    },
    {
      title: t('attendanceReport.exitTime'),
      dataIndex: 'exitTime',
      key: 'exitTime',
      render: (text: string, record: AttendanceDto) => {
        const editedBy = record.exitTimeUpdatedBy;
        const editedAt = record.exitTimeUpdatedAt;
        const originalExit = record.originalExitTime;
        const editorName =
          editedBy && (editedBy.firstName || editedBy.lastName)
            ? [editedBy.firstName, editedBy.lastName].filter(Boolean).join(' ')
            : null;
        if (!editedBy) {
          return <span>{text ? formatTime(text) : '–'}</span>;
        }
        const editedByLine = editorName
          ? `${editorName} ${t('attendanceReport.editedByPerson')}`
          : t('attendanceReport.exitEditedBy');
        const content = (
          <span className="d-inline-flex flex-column gap-0" style={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
            <span className="fw-medium">{text ? formatTime(text) : '–'}</span>
            <span className="text-muted" style={{ fontSize: '0.7rem' }}>{editedByLine}</span>
            {originalExit && (
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                {t('attendanceReport.originalExitTime')}: {formatOriginalExitTime(originalExit)}
              </span>
            )}
          </span>
        );
        if (!editedAt) return content;
        return (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`exit-edited-${record.id}`}>
                {t('attendanceReport.editedAtTooltip')}: {dayjs(editedAt).format('DD.MM.YYYY HH:mm')}
              </Tooltip>
            }
          >
            {content}
          </OverlayTrigger>
        );
      },
    },
    {
      title: t('attendanceReport.workTime'),
      dataIndex: 'totalWorkTime',
      key: 'totalWorkTime',
      render: (minutes: number, record: AttendanceDto) => {
        // Frontend'de hesaplanan çalışma süresi kullan
        const calculatedWorkTime = calculateWorkTime(record);
        return formatDuration(calculatedWorkTime);
      },
    },
    {
      title: t('attendanceReport.status'),
      dataIndex: 'isLate',
      key: 'isLate',
      render: (isLate: boolean, record: AttendanceDto) => (
        isLate ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-${record.id}`}>
                {record.excuses && record.excuses.length > 0
                  ? record.excuses.map(excuse => excuse.reason).join(', ')
                  : t('attendanceReport.noExcuse')}
              </Tooltip>
            }
          >
            <Tag color="red">
              {t('attendanceReport.late')} ({formatDuration(record.lateDuration)})
            </Tag>
          </OverlayTrigger>
        ) : (
          <Tag color="green">{t('attendanceReport.onTime')}</Tag>
        )
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Breadcrumb */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">{t('attendanceReport.pageTitle')}</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">{t('attendanceReport.breadcrumb.reports')}</li>
                <li className="breadcrumb-item active">{t('attendanceReport.breadcrumb.attendanceReport')}</li>
              </ul>
            </div>
          </div>
          {user?.permissions?.includes(PermissionType.EXPORT) && (
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap  ">
            <div className="dropdown me-2">
              <Link
                to="#"
                className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-file-export me-1" />
                {t('common.buttons.export')}
              </Link>
              <ul className="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <Link to="#" className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={handleExportPdf}
                  >
                    <i className="ti ti-file-type-pdf me-2 text-danger" />
                    {t('common.buttons.pdf')}
                  </Link>
                </li>
                <li className="mt-1">
                  <Link to="#" className="dropdown-item rounded-2 d-flex align-items-center py-2"
                    onClick={handleExportExcel}
                  >
                    <i className="ti ti-file-type-xls me-2 text-success" />
                    {t('common.buttons.excel')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          )}
        </div>

        {/* İstatistikler */}
        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-primary">
                    <i className="ti ti-users text-primary fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('attendanceReport.totalRecords')}</div>
                    <div className="dash-counts">
                      <h4>{filteredRecords.length}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-warning">
                    <i className="ti ti-clock-exclamation text-warning fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('attendanceReport.lateRecords')}</div>
                    <div className="dash-counts">
                      <h4>{filteredRecords.filter(r => r.isLate).length}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-success">
                    <i className="ti ti-clock-check text-success fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('attendanceReport.onTimeRecords')}</div>
                    <div className="dash-counts">
                      <h4>{filteredRecords.filter(r => !r.isLate).length}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="dash-widget-header">
                  <div className="avatar bg-light-info">
                    <i className="ti ti-chart-bar text-info fs-20" />
                  </div>
                  <div className="dash-count">
                    <div className="dash-title">{t('attendanceReport.averageLate')}</div>
                    <div className="dash-counts">
                      <h4>
                        {formatDuration(
                          Math.round(
                            filteredRecords.reduce((acc, curr) => acc + (curr.isLate ? curr.lateDuration : 0), 0) /
                            (filteredRecords.filter(r => r.isLate).length || 1)
                          )
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtreler ve Tablo */}
        <div className="card">
          {dateRange ? (
            <div className="card-body border-bottom pt-2 pb-2 bg-light">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar-event fs-16 me-2 text-primary"></i>
                <span className="fw-semibold text-muted me-1">{t('common.labels.dateRange', 'Tarih Aralığı')}:</span>
                <span className="text-dark fw-bold">
                  {dateRange[0].locale(i18n.language).format('DD MMMM YYYY')} - {dateRange[1].locale(i18n.language).format('DD MMMM YYYY')}
                </span>
              </div>
            </div>
          ) : (
            <div className="card-body border-bottom pt-2 pb-2 bg-light">
              <div className="d-flex align-items-center">
                <i className="ti ti-calendar-event fs-16 me-2 text-primary"></i>
                <span className="fw-semibold text-muted me-1">{t('common.labels.dateRange', 'Tarih Aralığı')}:</span>
                <span className="text-dark fw-bold">
                  {dayjs().startOf('day').locale(i18n.language).format('DD MMMM YYYY')} - {dayjs().endOf('day').locale(i18n.language).format('DD MMMM YYYY')}
                </span>
              </div>
            </div>
          )}
          <div className="card-header d-flex align-items-center justify-content-end flex-wrap row-gap-3">
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              <AttendanceFilters
                selectedDateRange={dateRange ? [dateRange[0].toISOString(), dateRange[1].toISOString()] : null}
                selectedDepartment={selectedDepartment}
                selectedShop={selectedShop}
                selectedGender={selectedGender}
                selectedLateStatus={filterLate}
                searchText={searchText}
                departments={departments}
                shops={shops}
                onDateRangeChange={(dates) => {
                  if (dates) {
                    const [start, end] = dates;
                    setDateRange([dayjs(start), dayjs(end)]);
                    fetchAttendanceRecords(dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD'));
                  } else {
                    setDateRange(null);
                    const today = dayjs();
                    fetchAttendanceRecords(
                      today.startOf('day').format('YYYY-MM-DD'),
                      today.endOf('day').format('YYYY-MM-DD')
                    );
                  }
                }}
                onDepartmentChange={setSelectedDepartment}
                onShopChange={setSelectedShop}
                onGenderChange={setSelectedGender}
                onLateStatusChange={setFilterLate}
                onSearchChange={setSearchText}
                onReset={handleReset}
              />
            </div>
          </div>

          <div className="table-responsive">
            {loading ? (
              <LoadingSpinner centered={true} />
            ) : (
              <Table
                columns={columns}
                dataSource={filteredRecords}
                showPageSize={true}
                showSearch={false}
              />
            )}
          </div>
        </div>
      </div>
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <FooterSection />
      </div>
    </div>
  );
};

export default AttendanceReports; 